import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  Button,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  InputAdornment,
  Tooltip,
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';

// Operatoren für die Bedingungen
const operators = [
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: '==', label: '=' },
  { value: '!=', label: '≠' },
];

// Logische Operatoren
const logicalOperators = [
  { value: 'AND', label: 'UND' },
  { value: 'OR', label: 'ODER' },
];

const CohortBuilder = ({
  selectedParameters,
  selectedListId,
  parameterLists,
  onLoadList,
  onSave,
  onCancel,
  initialData
}) => {
  const [cohortName, setCohortName] = useState('');
  const [cohortDescription, setCohortDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [localSelectedListId, setLocalSelectedListId] = useState(selectedListId || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyUnused, setShowOnlyUnused] = useState(false);
  const [includeConditions, setIncludeConditions] = useState([]);
  const [excludeConditions, setExcludeConditions] = useState([]);

  // Initialize from initialData (edit mode)
  useEffect(() => {
    if (initialData) {
      setCohortName(initialData.name || '');
      setCohortDescription(initialData.description || '');
      setIsPublic(initialData.isPublic !== undefined ? initialData.isPublic : true);
      setIncludeConditions(initialData.includeConditions || []);
      setExcludeConditions(initialData.excludeConditions || []);
      setLocalSelectedListId(initialData.parameterListId || '');
    }
  }, [initialData]);

  // Track which parameters are used
  const usedParameterIds = [
    ...includeConditions.map(c => c.parameterId),
    ...excludeConditions.map(c => c.parameterId)
  ];

  // Filter parameters
  const filteredParameters = selectedParameters.filter(param => {
    const matchesSearch = searchTerm === '' ||
      param.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (param.omopName && param.omopName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      param.id.toString().toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = !showOnlyUnused || !usedParameterIds.includes(param.id);

    return matchesSearch && matchesFilter;
  });

  // Add parameter to inclusion criteria
  const addToInclusion = (parameter) => {
    setIncludeConditions([
      ...includeConditions,
      {
        parameterId: parameter.id,
        parameterName: parameter.name,
        operator: '>',
        value: '',
        logicalOperator: 'AND'
      }
    ]);
  };

  // Add parameter to exclusion criteria
  const addToExclusion = (parameter) => {
    setExcludeConditions([
      ...excludeConditions,
      {
        parameterId: parameter.id,
        parameterName: parameter.name,
        operator: '>',
        value: '',
        logicalOperator: 'AND'
      }
    ]);
  };

  // Update condition
  const updateCondition = (type, index, field, value) => {
    if (type === 'include') {
      const newConditions = [...includeConditions];
      newConditions[index] = { ...newConditions[index], [field]: value };
      setIncludeConditions(newConditions);
    } else {
      const newConditions = [...excludeConditions];
      newConditions[index] = { ...newConditions[index], [field]: value };
      setExcludeConditions(newConditions);
    }
  };

  // Remove condition
  const removeCondition = (type, index) => {
    if (type === 'include') {
      setIncludeConditions(includeConditions.filter((_, i) => i !== index));
    } else {
      setExcludeConditions(excludeConditions.filter((_, i) => i !== index));
    }
  };

  // Check if parameter is used
  const isParameterUsed = (parameterId) => {
    return usedParameterIds.includes(parameterId);
  };

  // Handle list selection
  const handleListSelect = () => {
    if (!localSelectedListId) {
      alert('Bitte wählen Sie eine Parameterliste aus');
      return;
    }
    onLoadList(localSelectedListId);
  };

  // Save cohort
  const handleSave = () => {
    if (!cohortName.trim()) {
      alert('Bitte geben Sie einen Namen für die Kohorte ein');
      return;
    }

    if (!cohortDescription.trim()) {
      alert('Bitte geben Sie eine Beschreibung für die Kohorte ein');
      return;
    }

    if (includeConditions.length === 0) {
      alert('Bitte fügen Sie mindestens ein Einschlusskriterium hinzu');
      return;
    }

    // Check if all conditions have values
    const allIncludeComplete = includeConditions.every(c => c.value !== '');
    const allExcludeComplete = excludeConditions.every(c => c.value !== '');

    if (!allIncludeComplete || !allExcludeComplete) {
      alert('Bitte füllen Sie alle Werte für die Bedingungen aus');
      return;
    }

    onSave({
      name: cohortName,
      description: cohortDescription,
      isPublic,
      includeConditions,
      excludeConditions,
      parameterCount: selectedParameters.length,
      usedParameterCount: new Set(usedParameterIds).size
    });
  };

  // Render condition
  const renderCondition = (condition, index, type) => (
    <Box
      key={`${type}-${index}`}
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: type === 'include' ? 'rgba(72, 187, 120, 0.05)' : 'rgba(239, 68, 68, 0.05)',
        borderRadius: 1,
        border: `1px solid ${type === 'include' ? 'rgba(72, 187, 120, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <Typography variant="body2" fontWeight="bold" color="text.primary">
            {condition.parameterName}
          </Typography>
        </Grid>
        <Grid item xs={4} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Operator</InputLabel>
            <Select
              value={condition.operator}
              label="Operator"
              onChange={(e) => updateCondition(type, index, 'operator', e.target.value)}
            >
              {operators.map((op) => (
                <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={2}>
          <TextField
            fullWidth
            label="Wert"
            variant="outlined"
            size="small"
            value={condition.value}
            onChange={(e) => updateCondition(type, index, 'value', e.target.value)}
            type="number"
          />
        </Grid>
        {index < (type === 'include' ? includeConditions : excludeConditions).length - 1 && (
          <Grid item xs={4} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Verknüpfung</InputLabel>
              <Select
                value={condition.logicalOperator}
                label="Verknüpfung"
                onChange={(e) => updateCondition(type, index, 'logicalOperator', e.target.value)}
              >
                {logicalOperators.map((op) => (
                  <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={2}>
          <IconButton
            color="error"
            onClick={() => removeCondition(type, index)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );

  // If no parameters loaded, show list selection
  if (selectedParameters.length === 0) {
    const selectedList = parameterLists?.find(list => list.id === localSelectedListId);

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          Schritt 1: Parameterliste auswählen
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Wählen Sie eine Parameterliste aus dem Datenkatalog, die als Basis für Ihre Kohorte dient.
        </Typography>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <FormControl fullWidth>
              <InputLabel>Parameterliste</InputLabel>
              <Select
                value={localSelectedListId}
                label="Parameterliste"
                onChange={(e) => setLocalSelectedListId(e.target.value)}
              >
                {parameterLists?.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    <Box>
                      <Typography variant="body1">{list.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {list.description} • {list.parameterIds.length} Parameter
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedList && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Listendetails
                </Typography>
                <Typography variant="body2" paragraph>
                  {selectedList.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`${selectedList.parameterIds.length} Parameter`} size="small" color="primary" />
                  <Chip label={`Erstellt: ${selectedList.createdAt}`} size="small" variant="outlined" />
                  {selectedList.createdBy && (
                    <Chip label={`von ${selectedList.createdBy}`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleListSelect}
                disabled={!localSelectedListId}
                startIcon={<PlaylistPlayIcon />}
                fullWidth
              >
                Liste laden und weiter
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
              >
                Abbrechen
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Main builder view
  return (
    <Box>
      {/* Cohort Name and Description */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Kohorteninformationen
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Name der Kohorte"
            variant="outlined"
            value={cohortName}
            onChange={(e) => setCohortName(e.target.value)}
            placeholder="z.B. Sepsis-Patienten mit Hypotonie"
          />
          <TextField
            fullWidth
            label="Beschreibung"
            variant="outlined"
            value={cohortDescription}
            onChange={(e) => setCohortDescription(e.target.value)}
            placeholder="z.B. Patienten mit Sepsisdiagnose und systolischem Blutdruck unter 90 mmHg"
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, backgroundColor: 'background.default', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isPublic ? <PublicIcon color="success" /> : <LockIcon />}
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {isPublic ? 'Öffentliche Kohorte' : 'Private Kohorte'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isPublic
                        ? 'Diese Kohorte ist für alle Benutzer sichtbar und kann von anderen verwendet werden'
                        : 'Diese Kohorte ist nur für Sie sichtbar'}
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Box>
        </Box>
      </Paper>

      {/* Split View */}
      <Grid container spacing={3}>
        {/* Left Panel - Available Parameters */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Verfügbare Parameter
              <Chip
                label={`${selectedParameters.length} geladen`}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            </Typography>

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Parameter suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Filter Toggle */}
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<FilterListIcon />}
                label={showOnlyUnused ? 'Nur unbenutzte' : 'Alle anzeigen'}
                onClick={() => setShowOnlyUnused(!showOnlyUnused)}
                color={showOnlyUnused ? 'primary' : 'default'}
                variant={showOnlyUnused ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip
                label={`${usedParameterIds.length} verwendet`}
                size="small"
                color="success"
                sx={{ ml: 1 }}
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Parameter List */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {filteredParameters.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
                  Keine Parameter gefunden
                </Typography>
              ) : (
                <List dense>
                  {filteredParameters.map((param) => {
                    const used = isParameterUsed(param.id);
                    return (
                      <ListItem
                        key={param.id}
                        sx={{
                          mb: 1,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: used ? 'action.selected' : 'background.paper',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {param.name}
                            </Typography>
                            {used && (
                              <CheckCircleIcon color="success" fontSize="small" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {param.id}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<AddIcon />}
                              onClick={() => addToInclusion(param)}
                              fullWidth
                            >
                              Einschluss
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<AddIcon />}
                              onClick={() => addToExclusion(param)}
                              fullWidth
                            >
                              Ausschluss
                            </Button>
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Cohort Definition */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '600px', overflowY: 'auto' }}>
            {/* Inclusion Criteria */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
              Einschlusskriterien
              <Chip
                label={includeConditions.length}
                size="small"
                color="success"
                sx={{ ml: 1 }}
              />
            </Typography>

            {includeConditions.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                Fügen Sie Einschlusskriterien hinzu, indem Sie Parameter aus der linken Liste auswählen
              </Alert>
            ) : (
              <Box sx={{ mb: 3 }}>
                {includeConditions.map((condition, index) =>
                  renderCondition(condition, index, 'include')
                )}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Exclusion Criteria */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
              Ausschlusskriterien (optional)
              <Chip
                label={excludeConditions.length}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            </Typography>

            {excludeConditions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 3 }}>
                Keine Ausschlusskriterien definiert
              </Typography>
            ) : (
              <Box>
                {excludeConditions.map((condition, index) =>
                  renderCondition(condition, index, 'exclude')
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
        >
          Abbrechen
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!cohortName || !cohortDescription || includeConditions.length === 0}
          startIcon={<SaveIcon />}
          size="large"
        >
          Kohorte speichern
        </Button>
      </Box>
    </Box>
  );
};

export default CohortBuilder;
